import {
  MutationActions,
  queryClient,
  QueryKeys,
  useEntityMutation,
} from "@/api/core";
import { useApi } from "@/hooks";
import { Poll, pollSchema } from "@/schemas";

import { updateStatusCache } from "../../statuses/utils";

interface Variables {
  statusId: string;
  pollId: string;
  choices: string[];
}

function useVotePoll() {
  const api = useApi();

  return useEntityMutation({
    globalQueryKey: QueryKeys.polls.global,
    idAttributeFromVariables: "pollId",
    action: MutationActions.UPDATE,
    schema: pollSchema,
    mutationFn: (variables: Variables) =>
      api.post(`/api/v1/polls/${variables.pollId}/votes`, {
        choices: variables.choices,
      }),
    onMutate(variables) {
      const choices = variables.choices.map((choice) => Number(choice));

      const poll = queryClient.getQueryData(
        QueryKeys.polls.poll(variables.pollId),
      ) as Poll | undefined;

      if (!poll) return {};

      const updatedPoll: Poll = pollSchema.parse({
        ...poll,
        voted: true,
        own_votes: choices,
        voters_count: poll.voters_count + 1,
        votes_count: poll.votes_count + choices.length,
        options: poll.options.map((option, i) => ({
          ...option,
          votes_count: choices.includes(i)
            ? option.votes_count + 1
            : option.votes_count,
        })),
      });

      const [prevStatus] = updateStatusCache(variables.statusId, (status) => ({
        ...status,
        poll: updatedPoll,
      }));

      queryClient.setQueryData(
        QueryKeys.polls.poll(variables.pollId),
        updatedPoll,
      );

      return { prevStatus, prevPoll: poll };
    },
    onSuccess(response, variables) {
      updateStatusCache(variables.statusId, () => ({
        poll: pollSchema.parse(response.data),
      }));
    },
    onError(error, variables, context) {
      if (context && context.prevStatus && context.prevPoll) {
        updateStatusCache(context.prevStatus.id, () => context.prevStatus!);

        queryClient.setQueryData(
          QueryKeys.polls.poll(variables.pollId),
          context.prevPoll,
        );
      }
    },
  });
}

export { useVotePoll };
